import React, { useState, useEffect, useCallback } from "react";
import { Button, Stack, Typography, Box, Grid, Card, Paper, TableContainer, Table, TableBody, TableRow, TableCell, TableHead, TablePagination, IconButton, Tooltip, Chip, Rating } from "@mui/material";
import { GpsFixed as TargetIcon, Work as BusinessIcon, Person as PersonalIcon, Visibility as ViewIcon, CardMembership as PromoIcon, TrendingUp as RiseIcon, Percent as PercentIcon, Stars as BonusIcon, History as HistoryIcon, ChatBubbleOutline as FeedbackIcon } from '@mui/icons-material';
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";
import { getStatusColor } from "../../../utils/statusColor";
import { formatDate } from "../../../utils/dateFormat";
import StatsCard from "../../../components/common/StatsCard";

function MyPerformance() {
    const [loading, setLoading] = useState(true);
    const [goalStats, setGoalStats] = useState({ total: 0, business: 0, personal: 0, completion: 0 });
    const [goalsList, setGoalsList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 10, totalCount: 0 });

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const userId = userData?.id;

    const fetchGoalsData = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const countRes = await getData(API_ENDPOINTS.fetchEmployeeGoalCount(userId));
            if (countRes.status === "SUCCESS" && Array.isArray(countRes.result)) {
                const summary = countRes.result;
                const totalData = summary.find(s => s.type === "totalGoals");
                const businessData = summary.find(s => s.type === "businessGoals");
                const personalData = summary.find(s => s.type === "personalGoals");

                setGoalStats({
                    total: totalData?.totalCount || 0,
                    business: businessData?.totalCount || 0,
                    personal: personalData?.totalCount || 0,
                    completion: totalData?.completionPercentage || 0,
                    businessCompletion: businessData?.completionPercentage || 0,
                    personalCompletion: personalData?.completionPercentage || 0
                });
            }

            const listRes = await getData(API_ENDPOINTS.fetchEmployeeGoals(userId));
            if (listRes.status === "SUCCESS") {
                const goalsArray = listRes.result?.goals || [];
                setGoalsList(goalsArray);
                setPagination(prev => ({
                    ...prev,
                    totalCount: listRes.result?.totalGoals || goalsArray.length
                }));
            }
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchGoalsData();
    }, [fetchGoalsData]);

    if (loading) return <Loader />;

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>My Performance</Typography>
                <Typography variant="body2" color="text.secondary">Track your progress, goals, and career advancement.</Typography>
            </Box>

            {/* Performance Stats Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatsCard
                        title="Total Goals"
                        value={goalStats.total}
                        subtitle="Overall Goals"
                        progress={goalStats.completion}
                        color="#6366f1"
                        icon={<TargetIcon />}
                        showProgress
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatsCard
                        title="Business Goals"
                        value={goalStats.business}
                        subtitle="Business Alignment"
                        progress={goalStats.businessCompletion}
                        color="#2563eb"
                        icon={<BusinessIcon />}
                        showProgress
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <StatsCard
                        title="Personal Goals"
                        value={goalStats.personal}
                        subtitle="Self Development"
                        progress={goalStats.personalCompletion}
                        color="#f59e0b"
                        icon={<PersonalIcon />}
                        showProgress
                    />
                </Grid>
            </Grid>

            {/* Goals Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#1E293B' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Goal Name</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Category</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Timeline</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Rating</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Status</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#fff' }}>
                        {goalsList
                            .slice(pagination.page * pagination.limit, pagination.page * pagination.limit + pagination.limit)
                            .map((goal) => (
                                <TableRow key={goal.id} hover>
                                    <TableCell sx={{ fontWeight: 600 }}>{goal.title}</TableCell>
                                    <TableCell>{goal.goal_type}</TableCell>
                                    <TableCell>{formatDate(goal.start_date)} - {goal.end_date ? formatDate(goal.end_date) : 'N/A'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Rating value={Number(goal.Ratings) || 0} readOnly precision={0.5} size="small" sx={{ color: '#f59e0b' }} />
                                            <Typography variant="caption" fontWeight="700" sx={{ color: '#475569', bgcolor: '#f1f5f9', px: 1, borderRadius: 1 }}>
                                                {goal.Ratings || 0}/5
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        {(() => {
                                            const { bg, color } = getStatusColor(goal.status);
                                            return <Chip label={goal.status} size="small" sx={{ width: '130px', bgcolor: bg, color: color, fontWeight: 700, borderRadius: 2 }} />;
                                        })()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details">
                                            <IconButton size="small" color="primary"><ViewIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={pagination.totalCount}
                page={pagination.page}
                onPageChange={(_, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value, 10), page: 0 }))}
            />

            {/* Promotion & Bonus Section */}
            <Box sx={{ mt: 5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                    <PromoIcon sx={{ color: '#6366f1' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Promotion & Bonus Details
                    </Typography>
                </Stack>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatsCard title="Promotion Eligibility" value="Eligible" color="#10b981" icon={<PromoIcon />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatsCard title="Role Advancement" value="Senior" subtitle="Software Engineer" color="#6366f1" icon={<RiseIcon />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatsCard title="Salary Increment" value="+ 12%" color="#f59e0b" icon={<PercentIcon />} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <StatsCard title="Performance Bonus" value="$50,000" color="#3b82f6" icon={<BonusIcon />} />
                    </Grid>

                    {/* Feedback & Appraisals Placeholder Cards */}
                    <Grid size={{ xs: 12 }}>
                        <Card sx={{ borderRadius: 4, minHeight: 200, display: 'flex', flexDirection: 'column', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
                                <Typography variant="subtitle1" fontWeight="700">Appraisals</Typography>
                                <Button size="small" startIcon={<HistoryIcon />} sx={{ textTransform: 'none' }}>History</Button>
                            </Stack>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">No ratings available for this cycle.</Typography>
                            </Box>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Card sx={{ borderRadius: 4, minHeight: 200, display: 'flex', flexDirection: 'column', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9' }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9' }}>
                                <Typography variant="subtitle1" fontWeight="700">Manager Feedback</Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3, textAlign: 'center' }}>
                                <FeedbackIcon sx={{ color: '#e2e8f0', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">Pending manager review.</Typography>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

export default MyPerformance;